import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  Form,
  Button,
  Typography,
  Flex,
  Grid,
  DatePicker,
  InputNumber,
  Select,
  App,
  message,
} from "antd";
import InputField from "../../../components/Input/Input";
import { useForm } from "antd/es/form/Form";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrUpdateAdvanceTransaction,
  fetchAdvanceTransactionList,
  fetchContraCode,
  fetchFromAccounts,
  fetchToAccounts,
} from "../advanceTransactionThunks";
import {
  setSelectedAdvanceTransaction,
  setIsEditAdvanceTransaction,
} from "../advanceTransactionSlice";
import dayjs from "dayjs";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  fetchCustomers,
  fetchSuppliers,
} from "../../../store/commonThunks/lookups";
import {
  fetchTagGroups,
  fetchTagGroupFields,
} from "../../../store/commonThunks/lookups";
import { dateFormat } from "../../../lib/functions";
import AddNewModal from "../../../components/DropDown/AddNewModal";
import TagCreateForm from "../../Settings/Tags/components/TagCreateForm";
import { selectAuthInfo } from "../../Auth/authSelectors";
import useMigrationDate from "../../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const AdvanceTransactionForm = ({ onBack }) => {
  const { modal } = App.useApp();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const [form] = useForm();
  const [accName, accNameSet] = useState("");
  const [tagModal, setTagModal] = useState(false);

  // 🔹 Refs to track previous values for detecting user-driven changes in edit mode
  const prevTranType = useRef(null);
  const prevAccType = useRef(null);

  const {
    isEditAdvanceTransaction,
    selectedAdvanceTransaction,
    fromAccounts,
    tagFieldId,
  } = useSelector((s) => s.advanceTransaction);
  const lookups = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  const accType = Form.useWatch("acc_type", form);
  const tranType = Form.useWatch("tran_type", form);
  const accId = Form.useWatch("accId", form);

  const hasTranType = !!tranType || (isEditAdvanceTransaction && !!selectedAdvanceTransaction?.tran_type);
  const hasAccType = !!accType || (isEditAdvanceTransaction && !!selectedAdvanceTransaction?.acc_type);
  const hasAccId = !!accId || (isEditAdvanceTransaction && !!selectedAdvanceTransaction?.acc_id);
  const effectiveAccType = accType || (isEditAdvanceTransaction ? selectedAdvanceTransaction?.acc_type : "");
  /** 🔹 Load dropdowns */
  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchCustomers());
    dispatch(fetchFromAccounts());
    dispatch(fetchContraCode());
    dispatch(fetchTagGroups());
    dispatch(fetchTagGroupFields());
  }, [dispatch]);

  /** 🔹 Reset or set form values */
  useEffect(() => {
    if (isEditAdvanceTransaction && selectedAdvanceTransaction) {
      // Seed refs so first render doesn't trigger clearing
      prevTranType.current = selectedAdvanceTransaction.tran_type;
      prevAccType.current = selectedAdvanceTransaction.acc_type;

      form.setFieldsValue({
        tran_id: selectedAdvanceTransaction.tran_id,
        created_date: dayjs(selectedAdvanceTransaction.tran_date, "DD-MM-YYYY"),
        tran_type: selectedAdvanceTransaction.tran_type,
        acc_type: selectedAdvanceTransaction.acc_type,
        accId: selectedAdvanceTransaction.acc_id,
        tran_acc_id: selectedAdvanceTransaction.tran_acc_id,
        tran_amount: Number(selectedAdvanceTransaction.tran_amount),
        narration: selectedAdvanceTransaction.narration,
        tag_field_id: selectedAdvanceTransaction.tag_field_id,
      });

      accNameSet({
        display_text: selectedAdvanceTransaction.acc_name,
        sale_id: selectedAdvanceTransaction.acc_id,
      });
    } else {
      prevTranType.current = null;
      prevAccType.current = null;
      form.resetFields();
      accNameSet("");
      form.setFieldsValue({
        created_date: dayjs(),
      });
    }
  }, [isEditAdvanceTransaction, selectedAdvanceTransaction]);

  /** 🔹 tran_type change — clear acc_type, accId, tran_acc_id + accName */
  useEffect(() => {
    if (!tranType) return;

    if (prevTranType.current !== null && prevTranType.current !== tranType) {
      form.setFieldsValue({
        acc_type: undefined,
        accId: undefined,
        tran_acc_id: undefined,
      });
      accNameSet("");
    }

    prevTranType.current = tranType;
  }, [tranType]);

  /** 🔹 acc_type change — clear accId, tran_acc_id + accName */
  useEffect(() => {
    if (!accType) return;

    if (prevAccType.current !== null && prevAccType.current !== accType) {
      form.setFieldsValue({
        accId: undefined,
        tran_acc_id: undefined,
      });
      accNameSet("");
    }

    prevAccType.current = accType;
  }, [accType]);

  /** 🔹 accId change — clear tran_acc_id (create mode only, edit handled by above) */
  useEffect(() => {
    if (!accId && !isEditAdvanceTransaction) {
      form.setFieldsValue({
        tran_acc_id: undefined,
      });
    }
  }, [accId]);

  /** 🔹 Save */
  const handleSave = async (values) => {
    try {
      if (!values.accId) {
        modal.warning({
          title: "Please select a Customer or Supplier",
        });
        return;
      }
      const payload = {
        tran_date: values.created_date.format("DD-MM-YYYY"),
        tran_type: values.tran_type,
        acc_type: values.acc_type,
        acc_id: values.accId,
        tran_acc_id: values.tran_acc_id,
        tran_amount: values.tran_amount || 0,
        narration: values.narration || "",
        tran_id: selectedAdvanceTransaction?.tran_id || 0,
        action: isEditAdvanceTransaction ? "update" : "create",
        tag_field_id: values.tag_field_id,
      };

      await dispatch(createOrUpdateAdvanceTransaction(payload)).unwrap();

      modal.success({
        title: isEditAdvanceTransaction
          ? "Advance Transaction updated successfully"
          : "Advance Transaction created successfully",
      });

      dispatch(setSelectedAdvanceTransaction(null));
      dispatch(setIsEditAdvanceTransaction(false));
      onBack();
      dispatch(fetchAdvanceTransactionList());
      dispatch(fetchContraCode());
    } catch (err) {
      modal.warning({ title: err.message || "Save failed" });
    }
  };
const {disabledDate}= useMigrationDate();
  return (
    <div className="advance-transaction-form">
      <Title
        level={2}
        style={{
          marginBottom: 32,
          wordBreak: "break-word",
          fontSize: screens.md ? undefined : "20px",
        }}
      >
        {isEditAdvanceTransaction
          ? "Update Advance Transaction"
          : "Create New Advance Transaction"}
      </Title>

      <Form
        form={form}
        layout="vertical"
        data-testid="form-advance-transaction"
        onFinish={handleSave}
        style={{ maxWidth: "1400px" }}
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}>*</span>
            </>
          ) : (
            label
          )
        }
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: 24,
          }}
        >
          {/* LEFT SIDE */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              label="Transaction Type"
              name="tran_type"
              rules={[{ required: true }]}
            >
              <Select
                data-testid="select-tran-type"
                options={[
                  { label: "Payment", value: "payment" },
                  { label: "Receive", value: "receive" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Account Type"
              name="acc_type"
              rules={[{ required: true }]}
            >
              <Select
                data-testid="select-acc-type"
                disabled={!hasTranType}
                options={[
                  { label: "debitor", value: "debitor" },
                  { label: "creditor", value: "creditor" },
                ]}
              />
            </Form.Item>

            {/* 🔹 Visible field showing Customer/Supplier name */}
            <Form.Item label="Customer/Supplier" required>
              <SearchListAutoComplete
                entityType="Customer/Supplier"
                data-testid="input-customer-supplier"
                disabled={!hasAccType}
                optionsList={
                  effectiveAccType === "debitor"
                    ? (lookups.customers || []).map((e) => ({
                        sale_id: e.acc_id,
                        display_text: e.acc_name,
                      }))
                    : effectiveAccType === "creditor"
                    ? (lookups.suppliers || []).map((e) => ({
                        sale_id: e.acc_id,
                        display_text: e.acc_name,
                      }))
                    : []
                }
                placeholder="Customer/Supplier"
                showAddNew={false}
                value={accName}
                onSelect={(obj) => {
                  accNameSet(obj);
                  form.setFieldsValue({ accId: obj?.sale_id });
                }}
              />
            </Form.Item>

            {/* 🔹 Hidden field for storing ID in Form */}
            <Form.Item
              name="accId"
              hidden
              rules={[
                {
                  required: true,
                  message: "Please select a Customer or Supplier",
                },
              ]}
            />

            <Form.Item
              label="Transaction Date"
              name="created_date"
              rules={[{ required: true }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                data-testid="date-tran-date"
                format="DD/MM/YYYY"
                    disabledDate={disabledDate}  
              />
            </Form.Item>
          </Flex>

          {/* RIGHT SIDE */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "45%" }}
          >
            <Form.Item
              label="Cash/Bank Account"
              name="tran_acc_id"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select Account"
                disabled={!hasAccId}
                data-testid="select-cash-bank"
                options={fromAccounts.map((acc) => ({
                  label: acc.acc_name,
                  value: acc.acc_id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Amount"
              name="tran_amount"
              rules={[
                { required: true },
                { type: "number", min: 1, message: "Must be > 0" },
              ]}
            >
              <InputNumber
                data-testid="input-tran-amount"
                min={1}
                step={1}
                precision={2}
                style={{ width: "100%" }}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>

            <Form.Item label="Description" name="narration">
              <InputField
                placeholder="Enter Description"
                data-testid="input-description"
              />
            </Form.Item>

            <Form.Item label="Tags" name="tag_field_id">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                placeholder="Select Tag(s)"
                options={(lookups.tagFields || []).map((tag) => ({
                  label: tag.display_text,
                  value: tag.id,
                }))}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Flex style={{ marginBottom: "32px", width: "25%" }} gap={32}>
          <Button
            size="large"
            shape="default"
            className="traic-button"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="traic-button"
            data-testid="btn-save-advance-tran"
          >
            {isEditAdvanceTransaction ? "Update" : "Save"}
          </Button>
        </Flex>
      </Form>

      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t?.raw?.field_name?.toLowerCase() === createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              setTagModal(false);
              return;
            }

            const current = form.getFieldValue("tag_field_id") || [];

            if (current.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              setTagModal(false);
              return;
            }

            form.setFieldsValue({
              tag_field_id: [...current, match.id],
            });

            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default AdvanceTransactionForm;